import React, {useEffect, useState} from 'react';
import {getIntParamOrDefault, getStringParamOrDefault, updateQueryStringForState} from "../../../utils/urlBuilder";
import SelectDropdown from "../../Components/Dropdowns/SelectDropdown";
import {serviceRetailerDropdown} from "../../../utils/retailerDisplayFormatter";
import {getStoreTotalsQueries} from "./Components/StoreTotalsTotalDollarsQueries";
import StoreTotalsTotalDollars from "./Components/StoreTotalsTotalDollars";
import {makeApiCall} from "../../../Api/makeApiCall";
import IconImage, {Icons} from "../../Components/IconImage";
import {makeApolloClientCall} from "../../../Api/makeApolloApiCall";
import Modal from "react-modal";
import classnames from "classnames";
import Checkbox from "../../Components/Checkbox";
import ToggleRadioButtons from "../../Components/ToggleRadioButtons";
import {currentPeriodQuery} from "../../../queries/currentPeriodQuery";
import AdditionalPeriodsDropdown from "../Components/AdditionalPeriodsDropdown";
import Page from "../../Components/Page";
import {Breadcrumbs} from "../../Components/Breadcrumbs";
import moment from "moment/moment";
import _ from "lodash";
import {faIcons} from "../../../utils/iconConstants";

export const STORE_TOTALS_DEFAULT_SELECTIONS = {
    percentThreshold: 25,
    dataSelection: 'Dollars',
    includeAllData: false,
    includePlu: false,
    includeIfps: false,
    includeYago: false,
    productSnapshotOnly: false,
    additionalPeriods: {value: "2", label: "2"},
    includeFillOnly: false
}

const StoreTotals = () => {

    //region State Values
    const retailerOptions = serviceRetailerDropdown().data || []
    const today = moment(Date.now()).format('yyyy-MM-DD');

    const directSubmit = getStringParamOrDefault('directSubmit', false);
    const [retailerId, setRetailerId] = useState(getIntParamOrDefault('retailerId', undefined));

    const [selectedPeriod, setSelectedPeriod] = useState(getStringParamOrDefault('periodAbbreviation', undefined));
    const [startDate, setStartDate] = useState(undefined);
    const [endDate, setEndDate] = useState(undefined);
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

    const [dataSelection, setDataSelection] = useState(STORE_TOTALS_DEFAULT_SELECTIONS.dataSelection);
    const [includeFillOnly, setIncludeFillOnly] = useState(STORE_TOTALS_DEFAULT_SELECTIONS.includeFillOnly);
    const [includeAllData, setIncludeAllData] = useState(STORE_TOTALS_DEFAULT_SELECTIONS.includeAllData);
    const [includePlu, setIncludePlu] = useState(STORE_TOTALS_DEFAULT_SELECTIONS.includePlu);
    const [includeIfps, setIncludeIfps] = useState(STORE_TOTALS_DEFAULT_SELECTIONS.includeIfps);
    const [includeYago, setIncludeYago] = useState(STORE_TOTALS_DEFAULT_SELECTIONS.includeYago);
    const [productSnapshotOnly, setProductSnapshotOnly] = useState(STORE_TOTALS_DEFAULT_SELECTIONS.productSnapshotOnly);
    const [percentThreshold, setPercentThreshold] = useState(STORE_TOTALS_DEFAULT_SELECTIONS.percentThreshold);
    const [storeIdsFilter, setStoreIdsFilter] = useState(getStringParamOrDefault('storeIdsFilter', undefined));
    const [additionalPeriods, setAdditionalPeriods] = useState(STORE_TOTALS_DEFAULT_SELECTIONS.additionalPeriods);
    const [stateFilter, setStateFilter] = useState(getStringParamOrDefault('stateFilter', undefined));
    const [openStoresOnlyFilter, setOpenStoresOnlyFilter] = useState(false);

    const [dataFormat, setDataFormat] = useState('value');
    const [storeTotalsQueries, setStoreTotalsQueries] = useState(undefined);
    const [submittedSelections, setSubmittedSelections] = useState({
        dataSelection,
        includeFillOnly,
        salesFilters: {includePlu, includeIfps, productSnapshotOnly}
    });
    const [fillWatermark, setFillWatermark] = useState(undefined);
    const [promoWatermark, setPromoWatermark] = useState(undefined);
    const [stateFilterOptions, setStateFilterOptions] = useState([]);
    const [advancedPeriodOptions, setAdvancedPeriodOptions] = useState([]);
    const [periodOptions, setPeriodOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [triggerRefresh, setTriggerRefresh] = useState(Date.now());
    //endregion

    useEffect(() => {
        setLoading(true);
        setStoreTotalsQueries(undefined);

        Promise.all([
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/fill_watermark/`),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/promo_watermark/`),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/safetyDate`),
            makeApolloClientCall(`{stores{state}}`),
            makeApolloClientCall(currentPeriodQuery),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/timeService`),
            makeApolloClientCall(`{ periods(periodEndWeeksOnly: true) { firstWeekOfPeriod { endDate } endDate periodAbbreviation weekId quadId} }`)
        ]).then(([
                     fillWatermarkResults,
                     promoWatermarkResults,
                     safetyDateResults,
                     statesResults,
                     currentPeriodResults,
                     timeServiceResults,
                     periodListResults
                 ]) => {
            const safetyDate = safetyDateResults.safetyDate;
            const stateFilterOptions = [...new Set(statesResults.stores.map(store => store.state).filter(state => state && state !== '').sort())];
            const filteredMappedPeriods = periodListResults.periods
                .filter(period => period.firstWeekOfPeriod.endDate <= today && period.firstWeekOfPeriod.endDate >= safetyDate)
                .map(period => ({
                    value: period.periodAbbreviation,
                    label: period.periodAbbreviation,
                    startDate: period.firstWeekOfPeriod.endDate,
                    endDate: period.endDate,
                    quadId: period.quadId
                }));

            const maxDateForTimeService = _.maxBy(filteredMappedPeriods, 'endDate')
            const filteredMappedWeeks = timeServiceResults.data
                .filter(week => week.endDate <= maxDateForTimeService.endDate && week.endDate >= safetyDate)
                .map(week => ({value: week.endDate, label: week.periodWeek}));

            const periodAbbreviation1 = selectedPeriod ? selectedPeriod : currentPeriodResults.records[0]?.periodAbbreviation;
            const currentPeriodDetails = filteredMappedPeriods.find(period => period.value === periodAbbreviation1);

            setSelectedPeriod(currentPeriodDetails.value);
            setStartDate(currentPeriodDetails.startDate);
            setEndDate(currentPeriodDetails.endDate);

            setFillWatermark(fillWatermarkResults);
            setPromoWatermark(promoWatermarkResults);
            setStateFilterOptions(stateFilterOptions);
            setAdvancedPeriodOptions(filteredMappedWeeks);
            setPeriodOptions(filteredMappedPeriods);
            if (directSubmit) setShowAdvancedOptions(true);
        }).catch(() => setError(true))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        updateQueryStringForState({
            directSubmit: undefined,
            periodAbbreviation: selectedPeriod,
            retailerId,
            stateFilter,
            storeIdsFilter
        });
    }, [selectedPeriod, retailerId, stateFilter, storeIdsFilter]);

    const handleSubmit = () => {
        const endDateClause = !includeAllData && endDate ? `, endDate: "${endDate}"` : '';
        const startDateClause = !includeAllData && startDate ? `, startDate: "${startDate}"` : '';
        const stateFilterClause = stateFilter ? `, state: "${stateFilter}"` : '';
        const openStoresOnlyClause = openStoresOnlyFilter ? `, openStoresOnly: ${openStoresOnlyFilter}` : ''
        const storeIdsFilterClause = storeIdsFilter ? `, storeIds: [${storeIdsFilter}]` : '';

        const queries = getStoreTotalsQueries(dataSelection,
            retailerId,
            includePlu,
            includeIfps,
            includeYago,
            productSnapshotOnly,
            startDateClause,
            endDateClause,
            stateFilterClause,
            includeFillOnly,
            openStoresOnlyClause,
            storeIdsFilterClause,
            true);
        setStoreTotalsQueries(queries);
        setSubmittedSelections({
            dataSelection,
            includeFillOnly,
            salesFilters: {includePlu, includeIfps, productSnapshotOnly}
        });
        setShowAdvancedOptions(false);
        setTriggerRefresh(Date.now())
        updateQueryStringForState({pageIndex: 0})
    }

    const retailerOnChange = option => {
        setRetailerId(option.value);
        setStoreTotalsQueries(undefined);
    };

    const getDisabled = () => !(retailerId !== undefined && selectedPeriod !== undefined && startDate !== undefined && endDate !== undefined)

    return <Page name={"Store Totals"}
                 breadCrumbs={<Breadcrumbs items={[{label: "Retailer Readiness"}, {label: "Store Totals"}]}/>}
    >
        <div className={'store-totals-search-options-container'}>
            {!error && <div className={'store-totals-base-search-options'}>
                <SelectDropdown id={'retailer_base_search_dropdown'}
                                dataTestId={'retailer_base_search_dropdown'}
                                inputLabel={'Retailer'}
                                options={retailerOptions}
                                onChange={retailerOnChange}
                                selectValue={retailerId}
                                selectLabel={retailerOptions?.find(option => option.value === retailerId)?.label}
                />
                <SelectDropdown id={'period_base_search_dropdown'}
                                dataTestId={'period_base_search_dropdown'}
                                inputLabel={'Period'}
                                options={periodOptions}
                                onChange={(option) => {
                                    setSelectedPeriod(option.value);
                                    setStartDate(option.startDate);
                                    setEndDate(option.endDate);
                                }}
                                selectValue={selectedPeriod}
                                selectLabel={periodOptions?.find(option => option.value === selectedPeriod)?.label}
                />
                <div className={"store-totals-divider"}/>
                <button type="button"
                        data-testid={'store-totals-advanced-options-submit-button'}
                        className={classnames("button", "primary", "store-totals-advanced-options")}
                        onClick={() => handleSubmit()}
                        disabled={getDisabled()}
                >
                    {"Submit"}
                </button>
                <button className={'button store-totals-advanced-options advanced-options-button'}
                        data-testid={'store-totals-advanced-options-modal-button'}
                        onClick={() => setShowAdvancedOptions(true)}
                >
                    {!loading ? <IconImage iconType={Icons.ADVANCED_OPTIONS} className={'advanced-options-icon'}/> :
                        <div className={'store-totals-loading'}><i className={`${faIcons.carrot} fa-spin spinner`}/> Loading
                        </div>}
                </button>
            </div>}
            {showAdvancedOptions === true && <Modal clickable
                                                    isOpen={showAdvancedOptions}
                                                    className={'Modal totals-modal advanced-options-container'}
                                                    overlayClassName="Overlay"
                                                    ariaHideApp={false}
                                                    onRequestClose={() => setShowAdvancedOptions(false)}
                                                    testId={"store-totals-advanced-options-modal"}>
                <div className={'store-totals-search-options-container'}>
                    <div className={'store-totals-advanced-options-modal-header'}
                         data-testid={'confirmation-modal-header'}>
                        <h5>Advanced Options</h5>
                        <div className={'modal-exit-button'} data-testid={'confirmation-modal-exit-button'}>
                            <button className={"right-exit-button"}
                                    onClick={() => setShowAdvancedOptions(false)}
                                    disabled={loading}
                            >
                                <i data-testid={'x-icon'} className={faIcons.xClose}/></button>
                        </div>
                    </div>
                    <hr className={"draw-a-line"}/>
                    <div className={'advanced-options-container '}>
                        <div className={'advanced-options-basic-options-wrapper'}>
                            <h6>Basic Options</h6>
                            <div className={'advanced-options-basic-options-row'}>
                                <div className={'advanced-options-retailer-id-wrapper'}>
                                    <label id={'percent_threshold_label'} data-testid={'label-retailer-id'}>Retailer
                                        Id</label>
                                    <SelectDropdown id={'retailer_dropdown'}
                                                    options={retailerOptions}
                                                    onChange={retailerOnChange}
                                                    selectValue={retailerId}
                                                    selectLabel={retailerOptions?.find(option => option.value === retailerId)?.label}
                                    />
                                </div>
                            </div>
                            <div className={'advanced-options-basic-options-row'}>
                                <div className={'advanced-options-period-wrapper'}>
                                    <label id={'percent_threshold_label'} data-testid={'label-period'}>Select
                                        Period</label>
                                    <SelectDropdown id={'period_dropdown'}
                                                    dataTestId={"period_dropdown"}
                                                    options={periodOptions}
                                                    onChange={(option) => {
                                                        setSelectedPeriod(option.value);
                                                        setStartDate(option.startDate);
                                                        setEndDate(option.endDate);
                                                    }}
                                                    isDisabled={includeAllData}
                                                    selectValue={selectedPeriod}
                                                    selectLabel={periodOptions?.find(option => option.value === selectedPeriod)?.label}
                                                    width={250}
                                    />
                                </div>
                                <div className={'advanced-options-percent-threshold-wrapper'}
                                     data-testid={"advanced-options-percent-threshold-wrapper"}>
                                    <label id={'additional_periods_label'} data-testid={'additional-periods-label'}>Additional
                                        Past Periods to Query</label>
                                    <AdditionalPeriodsDropdown id={'additional_periods'}
                                                               onChange={(period) => setStartDate(period.endDate)}
                                                               endPeriod={periodOptions?.find(option => option.value === selectedPeriod)}
                                                               additionalPeriods={additionalPeriods}
                                                               additionalPeriodsUpdate={setAdditionalPeriods}/>
                                </div>
                            </div>
                        </div>
                        <hr className={"draw-a-line"}/>
                        <div className={'advanced-options-additional-data-selection-wrapper'}>
                            <h6>Data Selection</h6>
                            <ToggleRadioButtons id={'data_selection'} options={['Dollars', 'Units']}
                                                selectedOption={dataSelection}
                                                selectedCallback={setDataSelection}/>
                        </div>
                        <hr className={"draw-a-line"}/>
                        <div className={'advanced-options-additional-check-boxes-wrapper'}>
                            <h6>Filters</h6>
                            <div className={'advanced-options-additional-check-boxes-row'}>
                                <Checkbox id={'include_all_data'}
                                          labelText={'Return All Data'}
                                          onChange={setIncludeAllData}
                                          checked={includeAllData}/>
                                <Checkbox id={'include_plu'}
                                          labelText={'Include PLU'}
                                          onChange={setIncludePlu}
                                          checked={includePlu}/>
                                <Checkbox id={'include_ifps'}
                                          labelText={'Include Ifps'}
                                          onChange={setIncludeIfps}
                                          checked={includeIfps}/>
                                <Checkbox id={'product_snapshot_only'}
                                          labelText={'Product Snapshot Only'}
                                          onChange={setProductSnapshotOnly}
                                          checked={productSnapshotOnly}/>
                                <Checkbox id={'include_yago'}
                                          labelText={'Include YAGO'}
                                          onChange={setIncludeYago}
                                          checked={includeYago}/>
                                <Checkbox id={'include_fill_only'}
                                          labelText={'Filled Data'}
                                          onChange={setIncludeFillOnly}
                                          checked={includeFillOnly}/>
                                <Checkbox id={'only_open_stores'}
                                          labelText={'Only Open Stores'}
                                          onChange={setOpenStoresOnlyFilter}
                                          checked={openStoresOnlyFilter}/>
                            </div>

                        </div>
                        <hr className={"draw-a-line"}/>
                        <div className={'advanced-options-additional-advanced-advanced-options-wrapper'}>
                            <h6>Advanced Filters</h6>
                            <div className={'advanced-options-additional-advanced-advanced-options-row'}>
                                <div className={'advanced-filters-start-period-wrapper'}>
                                    <label id={'start_period_dropdown_label'}>Start Period</label>
                                    <SelectDropdown id={'start_period_dropdown'}
                                                    dataTestId={"start_period_dropdown"}
                                                    options={advancedPeriodOptions}
                                                    onChange={(option) => setStartDate(option.value)}
                                                    isDisabled={includeAllData}
                                                    selectValue={startDate}
                                                    selectLabel={advancedPeriodOptions?.find(option => option.value === startDate)?.label}
                                    />
                                </div>
                                <div className={'advanced-filters-end-period-wrapper'}>
                                    <label id={'start_period_dropdown_label'}>End Period</label>
                                    <SelectDropdown id={'end_period_dropdown'}
                                                    dataTestId={"end_period_dropdown"}
                                                    options={advancedPeriodOptions}
                                                    onChange={(option) => setEndDate(option.value)}
                                                    isDisabled={includeAllData}
                                                    selectValue={endDate}
                                                    selectLabel={advancedPeriodOptions?.find(option => option.value === endDate)?.label}
                                    />
                                </div>
                                <div className={'advanced-filters-state-wrapper'}>
                                    <label id={'start_period_dropdown_label'}>State</label>
                                    <SelectDropdown
                                        id={"state_filter_dropdown"}
                                        dataTestId={"state_filter_dropdown"}
                                        onChange={(option) => setStateFilter(option?.value)}
                                        options={stateFilterOptions.map((state) => ({value: state, label: state}))}
                                        selectValue={stateFilter}
                                        isClearable
                                        placeholder={"Select State..."}
                                    />
                                </div>
                                <div className={'advanced-filters-store-ids-wrapper'}>
                                    <label id={'store_ids_label'}>Store IDs</label>
                                    <input
                                        data-testid={'input-store-id'}
                                        id={'store_id'}
                                        type={'text'}
                                        value={storeIdsFilter}
                                        onChange={(event) => setStoreIdsFilter(event.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={"right-things"}>
                        <button className="button modal-button cancel" type="button" name="close"
                                onClick={() => setShowAdvancedOptions(false)}>
                            Cancel
                        </button>
                        <button type="button"
                                className={classnames("button", "modal-button", "confirm")}
                                data-testid={'store-totals-advanced-options-modal-submit-button'}
                                onClick={() => handleSubmit()}
                                disabled={getDisabled()}
                        >
                            {"Submit"}
                        </button>
                    </div>
                </div>
            </Modal>}
            {error &&
                <div className={'error-message'} data-testid={'error-message'}>There was an error loading the data.
                    Please try again later.</div>}
            {storeTotalsQueries && <StoreTotalsTotalDollars selectedData={submittedSelections.dataSelection}
                                                     retailerId={retailerId}
                                                     percentThreshold={percentThreshold}
                                                     onPercentThresholdChange={setPercentThreshold}
                                                     dataFormat={dataFormat}
                                                     onDataFormatChange={setDataFormat}
                                                     queries={storeTotalsQueries}
                                                     includeFillOnly={submittedSelections.includeFillOnly}
                                                     salesFilters={submittedSelections.salesFilters}
                                                     fillWatermark={fillWatermark}
                                                     promoWatermark={promoWatermark}
                                                     reloadData={triggerRefresh}/>
            }
        </div>
    </Page>
};

export default StoreTotals;